import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GuidedTourRunnerService } from '../../../core/services/guided-tour';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-guided-tour-overlay',
  imports: [CommonModule, TranslateModule],
  // La convention du projet range les liaisons d'hôte ici plutôt que dans des
  // décorateurs @HostListener.
  host: {
    '(window:resize)': 'onResize()',
    '(window:scroll)': 'onScroll()',
    '(document:keydown.escape)': 'onEscape()',
    '(document:keydown.arrowright)': 'onRight()',
    '(document:keydown.arrowleft)': 'onLeft()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (runner.active() && runner.step(); as step) {
      <div class="fixed inset-0 z-[10000] pointer-events-none" role="dialog" aria-modal="true" [attr.aria-label]="'Visite guidée : ' + step.title">
        @if (runner.targetRect(); as rect) {
          <div class="tour-shade" [style.left.px]="0" [style.top.px]="0" [style.width]="'100%'" [style.height.px]="shadeTop(rect)"></div>
          <div class="tour-shade" [style.left.px]="0" [style.top.px]="shadeTop(rect)" [style.width.px]="shadeLeft(rect)" [style.height.px]="shadeHeight(rect)"></div>
          <div class="tour-shade" [style.left.px]="shadeRight(rect)" [style.top.px]="shadeTop(rect)" [style.right.px]="0" [style.height.px]="shadeHeight(rect)"></div>
          <div class="tour-shade" [style.left.px]="0" [style.top.px]="shadeBottom(rect)" [style.width]="'100%'" [style.bottom.px]="0"></div>
          <div class="fixed rounded-xl border-2 border-primary-border tour-target-ring transition-all duration-200"
            [style.left.px]="shadeLeft(rect)" [style.top.px]="shadeTop(rect)" [style.width.px]="shadeWidth(rect)" [style.height.px]="shadeHeight(rect)"></div>
        } @else {
          <div class="tour-shade inset-0"></div>
        }

        <section class="pointer-events-auto fixed bg-panel rounded-2xl shadow-2xl border border-hairline overflow-x-hidden overflow-y-auto w-[min(92vw,430px)]"
          [style.left]="panelStyle().left"
          [style.top]="panelStyle().top"
          [style.transform]="panelStyle().transform ?? null"
          [style.maxHeight]="panelStyle().maxHeight ?? null">
          <div class="h-1.5 bg-chrome"><div class="h-full bg-primary-base transition-all" [style.width.%]="progress()"></div></div>

          <div class="flex items-center justify-between px-5 pt-4">
            <button type="button" class="inline-flex min-h-11 items-center gap-2 text-xs font-bold text-primary-text hover:bg-primary-surface rounded-lg px-2 py-1.5"
              (click)="runner.toggleToc()" [attr.aria-expanded]="runner.tocOpen()">
              <i class="pi pi-list"></i> Sommaire
            </button>
            <button type="button" class="w-11 h-11 rounded-full hover:bg-chrome text-ink-muted" [attr.aria-label]="'MISC.CLOSE_TOUR' | translate" (click)="runner.close()">
              <i class="pi pi-times"></i>
            </button>
          </div>

          @if (runner.tocOpen()) {
            <nav class="mx-5 mt-3 p-2 bg-chrome rounded-xl border border-hairline max-h-52 overflow-y-auto" [attr.aria-label]="'MISC.TOUR_SUMMARY' | translate">
              @for (item of runner.scenario()!.steps; track item.id; let index = $index) {
                <button type="button" (click)="runner.goTo(index); runner.tocOpen.set(false)"
                  class="w-full text-left flex items-center gap-3 p-2.5 rounded-lg text-sm transition-colors"
                  [class.bg-primary-surface]="index === runner.stepIndex()" [class.text-primary-text]="index === runner.stepIndex()"
                  [class.hover:bg-neutral-surface]="index !== runner.stepIndex()">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black"
                    [class.bg-primary-base]="index === runner.stepIndex()" [class.text-on-primary]="index === runner.stepIndex()"
                    [class.bg-panel]="index !== runner.stepIndex()" [class.text-ink-muted]="index !== runner.stepIndex()">{{ index + 1 }}</span>
                  <span class="font-semibold">{{ item.title }}</span>
                </button>
              }
            </nav>
          }

          <div class="px-6 py-5">
            <p class="text-[11px] font-black uppercase tracking-[.18em] text-primary-text mb-2">Étape {{ runner.stepIndex() + 1 }} sur {{ runner.total() }}</p>
            <h2 class="text-xl font-extrabold text-ink">{{ step.title }}</h2>
            <p class="mt-2 text-sm leading-6 text-ink-muted">{{ step.content }}</p>
          </div>

          <footer class="flex flex-wrap items-center justify-between gap-3 px-5 py-4 bg-chrome border-t border-hairline">
            <button type="button" class="min-h-11 px-3 py-2 text-sm font-bold text-ink-muted hover:bg-neutral-surface rounded-lg disabled:opacity-30"
              [disabled]="runner.stepIndex() === 0" (click)="runner.previous()"><i class="pi pi-arrow-left mr-2"></i>{{ 'MISC.PREVIOUS' | translate }}</button>
            <button type="button" class="min-h-11 px-4 py-2.5 text-sm font-bold text-on-primary bg-primary-base hover:bg-primary-hover rounded-lg shadow-sm" (click)="runner.next()">
              {{ runner.stepIndex() === runner.total() - 1 ? 'Terminer' : 'Suivant' }}
              <i [class]="runner.stepIndex() === runner.total() - 1 ? 'pi pi-check ml-2' : 'pi pi-arrow-right ml-2'"></i>
            </button>
          </footer>
        </section>
      </div>
    }
  `,
  styles: [`
    .tour-target-ring { box-shadow: 0 0 0 4px color-mix(in srgb, var(--color-primary-base) 25%, transparent); }
    .tour-shade { position: fixed; background: rgba(15, 23, 42, .68); pointer-events: auto; transition: all .2s ease; }
    @media (prefers-reduced-motion: reduce) { * { transition: none !important; scroll-behavior: auto !important; } }
  `],
})
export class GuidedTourOverlayComponent {
  readonly runner = inject(GuidedTourRunnerService);
  readonly progress = computed(() => ((this.runner.stepIndex() + 1) / Math.max(this.runner.total(), 1)) * 100);
  private readonly gap = 8;

  onResize() { this.runner.refreshTarget(); }
  onScroll() { this.runner.refreshTarget(); }
  onEscape() { this.runner.close(); }
  onRight() { if (this.runner.active()) this.runner.next(); }
  onLeft() { if (this.runner.active()) this.runner.previous(); }

  shadeTop(r: DOMRect) { return Math.max(0, r.top - this.gap); }
  shadeLeft(r: DOMRect) { return Math.max(0, r.left - this.gap); }
  shadeWidth(r: DOMRect) { return Math.min(innerWidth - this.shadeLeft(r), r.width + this.gap * 2); }
  shadeHeight(r: DOMRect) { return Math.min(innerHeight - this.shadeTop(r), r.height + this.gap * 2); }
  shadeRight(r: DOMRect) { return this.shadeLeft(r) + this.shadeWidth(r); }
  shadeBottom(r: DOMRect) { return this.shadeTop(r) + this.shadeHeight(r); }

  panelStyle(): Record<string, string> {
    const r = this.runner.targetRect();
    const placement = this.runner.step()?.placement ?? 'auto';
    if (!r || placement === 'center' || innerWidth < 700) return {
      left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
      maxHeight: 'calc(100dvh - 36px)',
    };
    const width = 430, height = this.runner.tocOpen() ? 520 : 340, margin = 18;
    let left = Math.min(Math.max(margin, r.left), innerWidth - width - margin);
    let top = r.bottom + margin;
    const chosen = placement === 'auto' ? (r.bottom + height < innerHeight ? 'bottom' : 'top') : placement;
    if (chosen === 'top') top = r.top - height - margin;
    if (chosen === 'left') { left = r.left - width - margin; top = r.top; }
    if (chosen === 'right') { left = r.right + margin; top = r.top; }
    left = Math.min(Math.max(margin, left), innerWidth - width - margin);
    top = Math.min(Math.max(margin, top), innerHeight - Math.min(height, innerHeight - margin * 2) - margin);
    return { left: `${left}px`, top: `${top}px`, maxHeight: `calc(100dvh - ${margin * 2}px)` };
  }
}

